"use client";

import Image from "next/image";
import { match, P } from "ts-pattern";

import { ForgeTNS } from "@coinbot/forge-api/models";

import CheckGradient from "~/assets/check-gradient.svg";
import runeIcon from "~/assets/rune-icon-color.png";
import ThorLogo from "~/assets/thor-logo.svg";
import X from "~/assets/x.svg";
import { Badge, Button } from "~/components/ui";
import { getShortWalletAddress, useSwapKit } from "~/context/swapkit";
import { PriceWithCurrency } from "./PriceWithCurrency";

export type ListingRowItemProps = {
  index?: number;
  onRemove?: () => void;
  tns?: ForgeTNS;
};

export function ListingRowItem({ index, tns, onRemove }: ListingRowItemProps) {
  const swapkit = useSwapKit();
  const showRemoveButton = !!onRemove && typeof onRemove === "function";
  const tnsName =
    tns?.name && tns?.name?.length > 16
      ? getShortWalletAddress(tns?.name, { endLength: 4 })
      : tns?.name;

  return (
    <div className="flex w-full items-center space-x-2.5 p-2 md:space-x-6">
      {index !== undefined && <div className="w-2 text-lg md:w-3 md:text-2xl">{index + 1}</div>}

      <div className="flex h-8 w-8 items-center justify-center bg-dark-subtle-gradient p-2 md:h-16 md:w-16 md:p-3.5">
        <ThorLogo />
      </div>

      <div className="flex flex-1 items-center">
        <div>
          <div className="flex items-center">
            <div className="text-sm font-semibold tracking-wide md:text-lg">{tnsName}.thor</div>

            <CheckGradient className="ml-1" />
          </div>

          {tns?.price && (
            <div className="mt-1.5 flex items-center text-xs text-[#F7FAFD]/50 md:mt-0 md:text-base">
              Floor:{" "}
              <Image src={runeIcon} priority alt="rune" className="ml-2 mr-1 w-[14px] opacity-50" />{" "}
              {tns?.price?.rune}
            </div>
          )}
        </div>

        {match(tns)
          .with({ price: P.not(P.nullish) }, () => (
            // TODO: refactor Price component, use composition
            <PriceWithCurrency price={tns?.price || undefined} className="!ml-auto" />
          ))
          .with({ owner: P.nullish }, () => (
            <Badge variant="success" className="ml-auto">
              Available
            </Badge>
          ))
          .with({ owner: swapkit.connectedWallet?.address }, () => (
            <Badge variant="secondary" className="ml-auto">
              Owned by you
            </Badge>
          ))
          .otherwise(() => (
            <Badge variant="destructive" className="ml-auto">
              Not available
            </Badge>
          ))}
      </div>

      {showRemoveButton && (
        <Button
          variant="icon"
          className="!ml-5 mb-auto mt-3 text-[#6F767E]"
          onClick={() => {
            onRemove?.();
          }}
        >
          <X />
        </Button>
      )}

      {tns?.status === "inactive" && (
        <div className="!ml-auto bg-black/20 p-2.5 text-sm capitalize text-[#F7FAFD]">
          Registered
        </div>
      )}
    </div>
  );
}
